// "I'm interested" counter for coming-soon bracket variants.
//
// Reads window.FND_FIREBASE_CONFIG (set in layout/theme.liquid from
// Settings > Interest counter (Firebase)) and does nothing if it's
// missing, so the page still renders cleanly before Firebase is wired up.
//
// Firestore security rules should restrict writes to +1/-1 steps only,
// with the count never going negative, e.g.:
//   match /{collection}/{variantId} {
//     allow read: if true;
//     allow create: if request.resource.data.keys().hasOnly(['count'])
//                   && request.resource.data.count == 1;
//     allow update: if request.resource.data.keys().hasOnly(['count'])
//                   && request.resource.data.count >= 0
//                   && (request.resource.data.count == resource.data.count + 1
//                       || request.resource.data.count == resource.data.count - 1);
//     allow delete: if false;
//   }

import { initializeApp, getApps } from 'https://www.gstatic.com/firebasejs/12.19.0/firebase-app.js';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  increment,
} from 'https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js';

(function () {
  'use strict';

  const CONFIG = window.FND_FIREBASE_CONFIG;
  const COLLECTION = (CONFIG && CONFIG.collection) || 'interested_counts';
  const COOKIE_DAYS = 3650;

  let db = null;

  function getDb() {
    if (!CONFIG || !CONFIG.apiKey || !CONFIG.projectId || !CONFIG.appId) return null;
    if (!db) {
      const app = getApps().length ? getApps()[0] : initializeApp(CONFIG);
      db = getFirestore(app);
    }
    return db;
  }

  function setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
  }

  function clearCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
  }

  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function kickFeather() {
    if (window.feather) window.feather.replace();
  }

  function createButton(container) {
    container.innerHTML = `
      <button type="button" class="btn interest-counter__btn">
        <i class="interest-counter__icon" data-feather="check"></i>
        <span class="interest-counter__label">People interested</span>
        <span class="interest-counter__count"></span>
      </button>
    `;
    return container.querySelector('.interest-counter__btn');
  }

  function updateButton(btn, count, isInterested, isBusy) {
    const countEl = btn.querySelector('.interest-counter__count');

    btn.disabled = isBusy || count === null;
    btn.classList.toggle('is-interested', !!isInterested);

    const newCount = count === null ? '…' : String(count);
    if (countEl.textContent !== newCount) {
      countEl.textContent = newCount;
      if (count !== null) {
        countEl.classList.remove('is-bumping');
        void countEl.offsetWidth; // restart the CSS animation
        countEl.classList.add('is-bumping');
      }
    }

    kickFeather();
  }

  async function handleClick(btn, ref, cookieName, state) {
    if (state.busy) return;
    state.busy = true;

    const goingInterested = !state.interested;
    const delta = goingInterested ? 1 : -1;
    const optimisticCount = Math.max(0, state.count + delta);

    updateButton(btn, optimisticCount, goingInterested, true);

    try {
      await setDoc(ref, { count: increment(delta) }, { merge: true });
      state.count = optimisticCount;
      state.interested = goingInterested;
      if (goingInterested) setCookie(cookieName, '1', COOKIE_DAYS);
      else clearCookie(cookieName);
    } catch (err) {
      console.error('FND interest counter: failed to update interest', err);
    }

    state.busy = false;
    updateButton(btn, state.count, state.interested, false);
  }

  async function mount(container) {
    if (!container || container.dataset.interestCounterMounted === 'true') return;
    container.dataset.interestCounterMounted = 'true';

    const variantId = container.dataset.variantId;
    if (!variantId) return;

    const firestore = getDb();
    if (!firestore) {
      container.remove();
      return;
    }

    const cookieName = `fnd_interested_${variantId}`;
    const state = { count: 0, interested: getCookie(cookieName) === '1', busy: false };

    const btn = createButton(container);
    updateButton(btn, null, state.interested, true);

    const ref = doc(firestore, COLLECTION, String(variantId));
    try {
      const snap = await getDoc(ref);
      state.count = snap.exists() && typeof snap.data().count === 'number' ? snap.data().count : 0;
    } catch (err) {
      console.error('FND interest counter: failed to read count', err);
    }

    // A cookie claiming "interested" can't be true if the count is 0 — that
    // combination only happens after the counter's been reset (e.g. manual
    // cleanup in Firestore) without also clearing visitors' cookies. Trying
    // to "unclick" in that state would try to push the count negative,
    // which the security rules correctly reject, leaving the button stuck.
    // Self-heal instead: treat it as not-yet-interested so the next click
    // registers real interest rather than failing silently.
    if (state.interested && state.count === 0) {
      state.interested = false;
      clearCookie(cookieName);
    }

    updateButton(btn, state.count, state.interested, false);
    btn.addEventListener('click', () => handleClick(btn, ref, cookieName, state));
  }

  function mountAll(root) {
    (root || document).querySelectorAll('.interest-counter[data-variant-id]').forEach(mount);
  }

  window.FND_InterestCounter = { mount, mountAll };

  document.addEventListener('DOMContentLoaded', () => mountAll());
})();
